import { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from "chart.js";
import axios from "axios";
import BASE_URL from "../config";
import useInView from "../hooks/useInView";
import { money, isToday } from "../utils/format";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

const GREEN = "#15633e";
const RED = "#9e1b1b";
const INTERVALS = [10, 20, 30, 40, 50]; // minutes

function Change({ value }) {
  if (value === null || value === undefined || Number.isNaN(value)) return <span>—</span>;
  const up = value >= 0;
  return (
    <span className={up ? "up-text" : "down-text"}>
      {up ? "▲" : "▼"} {Math.abs(value).toFixed(2)}%
    </span>
  );
}

export default function StockCard({ stock, showIncomplete, onSelect, onStale }) {
  const [ref, inView] = useInView();
  const [points, setPoints] = useState(null); // [{t: Date, c: number}]
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!inView || points || error) return;
    let cancelled = false;
    axios
      .get(`${BASE_URL}/intraday/${stock.ticker}`)
      .then((res) => {
        if (cancelled) return;
        const raw = Array.isArray(res.data) ? res.data : [];
        const parsed = raw
          .filter((d) => typeof d.Close === "number")
          .map((d) => ({ t: new Date(d.Date), c: d.Close }));
        setPoints(parsed);
        // Report if this session isn't today's (market closed).
        if (parsed.length) {
          const lastBar = parsed[parsed.length - 1].t;
          if (!isToday(lastBar)) onStale?.(lastBar);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        console.error(e);
        setError(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, points, error, stock.ticker]);

  // Derived metrics from intraday series.
  const last = points && points.length ? points[points.length - 1].c : null;
  const first = points && points.length ? points[0].c : null;
  const sessionChange = first && last ? ((last - first) / first) * 100 : null;
  const incomplete = points !== null && points.length < 5;

  const intervalChanges = [];
  if (points && points.length >= 2) {
    const now = points[points.length - 1].t;
    for (const minutesAgo of INTERVALS) {
      const target = new Date(now.getTime() - minutesAgo * 60000);
      let nearest = points[0];
      let best = Math.abs(points[0].t - target);
      for (const p of points) {
        const diff = Math.abs(p.t - target);
        if (diff < best) {
          best = diff;
          nearest = p;
        }
      }
      if (nearest && nearest.c) {
        intervalChanges.push({
          minutesAgo,
          change: ((last - nearest.c) / nearest.c) * 100,
        });
      }
    }
  }

  const showChart = points && points.length >= 2 && (!incomplete || showIncomplete);

  const chartData = showChart
    ? {
        labels: points.map((p) =>
          p.t.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
        ),
        datasets: [
          {
            data: points.map((p) => p.c),
            borderWidth: 1.5,
            pointRadius: 0,
            pointHoverRadius: 3,
            tension: 0.3,
            fill: true,
            backgroundColor: sessionChange >= 0 ? "rgba(21,99,62,0.10)" : "rgba(158,27,27,0.10)",
            segment: {
              borderColor: (ctx) => (ctx.p1.parsed.y >= ctx.p0.parsed.y ? GREEN : RED),
            },
          },
        ],
      }
    : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 250 },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#121212",
        cornerRadius: 0,
        displayColors: false,
        callbacks: { label: (i) => `$${Number(i.parsed.y).toFixed(2)}` },
      },
    },
    scales: { x: { display: false }, y: { display: false } },
  };

  return (
    <article
      ref={ref}
      className="stock-card"
      onClick={() => onSelect?.(stock.ticker)}
      title={`Open ${stock.ticker} in dashboard`}
    >
      <div className="stock-card-head">
        <div className="stock-id">
          <span className="stock-ticker">{stock.ticker}</span>
          <span className="stock-company">{stock.company}</span>
        </div>
        <div className="stock-day mono">
          <Change value={stock.change_percent} />
          <span className="stock-day-label">today</span>
        </div>
      </div>

      <div className="stock-price mono">{money(stock.price, stock.currency)}</div>

      <div className="stock-chart">
        {showChart ? (
          <Line data={chartData} options={chartOptions} />
        ) : error ? (
          <span className="stock-note">No intraday data</span>
        ) : points === null ? (
          <span className="skeleton stock-chart-sk" />
        ) : (
          <span className="stock-note">Limited intraday data</span>
        )}
      </div>

      {intervalChanges.length > 0 && (
        <div className="stock-intervals mono">
          {intervalChanges.map((iv) => (
            <span key={iv.minutesAgo} className="iv">
              <span className="iv-min">{iv.minutesAgo}m</span>
              <Change value={iv.change} />
            </span>
          ))}
        </div>
      )}
    </article>
  );
}
